import { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import LinearProgress from "@mui/material/LinearProgress";
import {
  getSettings,
  updateSettings,
  type UserSettings,
} from "@/api/dashboard";
import { useThemeMode } from "@/theme/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";

export default function SettingsPage() {
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [snackOpen, setSnackOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { mode, preference, setMode } = useThemeMode();
  const { user } = useAuth();

  useEffect(() => {
    let canceled = false;
    getSettings()
      .then((data) => {
        if (canceled) return;
        setSettings(data);
        setDisplayName(data.displayName ?? "");
      })
      .catch((err) => {
        if (canceled) return;
        setError(
          err instanceof Error ? err.message : "Failed to load settings",
        );
      })
      .finally(() => {
        if (!canceled) setLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, []);

  const handleSaveProfile = async () => {
    try {
      await updateSettings({ displayName });
      setSnackOpen(true);
    } catch {
      setError("Failed to save settings");
    }
  };

  const handleNotificationChange = async (field: string, checked: boolean) => {
    try {
      await updateSettings({ [field]: checked });
      setSettings((prev) => (prev ? { ...prev, [field]: checked } : prev));
    } catch {
      setError("Failed to update notification setting");
    }
  };

  return (
    <Box sx={{ maxWidth: 720, mx: "auto", py: 3, px: 2 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 3 }}>
        Settings
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3 }}
      >
        <Tab label="Profile" />
        <Tab label="Appearance" />
        <Tab label="Notifications" />
      </Tabs>

      {loading && <LinearProgress />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Profile tab */}
      {!loading && tab === 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Email"
            value={user?.email ?? settings?.email ?? ""}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <Box
            sx={{
              mt: 2,
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Typography variant="body2">
              To change your password, use the{" "}
              <Link component={RouterLink} to="/login?reset=1">
                password reset link
              </Link>{" "}
              on the login page.
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleSaveProfile}
            sx={{ alignSelf: "flex-start", mt: 1 }}
          >
            Save
          </Button>
        </Box>
      )}

      {/* Appearance tab */}
      {tab === 1 && (
        <Box>
          <ToggleButtonGroup
            value={preference}
            exclusive
            onChange={(_, val) => {
              if (val) setMode(val);
            }}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="light">Light</ToggleButton>
            <ToggleButton value="dark">Dark</ToggleButton>
            <ToggleButton value="system">System</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="body2" color="text.secondary">
            Current mode: {mode}
          </Typography>
        </Box>
      )}

      {/* Notifications tab */}
      {tab === 2 && settings && (
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={settings.notifyQuota80}
                onChange={(_, checked) =>
                  handleNotificationChange("notifyQuota80", checked)
                }
              />
            }
            label="Quota at 80%"
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.notifyQuota90}
                onChange={(_, checked) =>
                  handleNotificationChange("notifyQuota90", checked)
                }
              />
            }
            label="Quota at 90%"
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.notifyQuota100}
                onChange={(_, checked) =>
                  handleNotificationChange("notifyQuota100", checked)
                }
              />
            }
            label="Quota at 100%"
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.notifyFailedRenders}
                onChange={(_, checked) =>
                  handleNotificationChange("notifyFailedRenders", checked)
                }
              />
            }
            label="Failed renders"
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.notifyBillingEvents}
                onChange={(_, checked) =>
                  handleNotificationChange("notifyBillingEvents", checked)
                }
              />
            }
            label="Billing events"
          />
        </FormGroup>
      )}

      {/* Danger zone */}
      <Card variant="outlined" sx={{ mt: 6, borderColor: "error.main" }}>
        <CardContent>
          <Typography variant="h6" component="h2" color="error" sx={{ mb: 2 }}>
            Delete Account
          </Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete Account
          </Button>
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Account deletion is not yet self-serve. Email{" "}
            <Link href="mailto:support@pdfglyph.com">support@pdfglyph.com</Link>{" "}
            and we&apos;ll process the request within one business day.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
      >
        <Alert severity="success" onClose={() => setSnackOpen(false)}>
          Settings saved successfully.
        </Alert>
      </Snackbar>
    </Box>
  );
}
